'use client'

import { Box, Chip, Container, Grid, styled } from '@mui/material'
import { useSession } from 'next-auth/react'

import { BreadcrumbsCustom } from '@/components/BreadcrumbsCustom/BreadcrumbsCustom'
import { ContentSanityComponent } from '@/components/SanityComponent/ContentSanityComponent'
import { getArticleFromSanityArticles } from '@/helpers/getArticleFromSanityArticles'
import { getResponsiveBackgroundImage } from '@/helpers/getResponsiveBackgroundImage'
import { resolveSanityImage } from '@/helpers/resolveSanityImage'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTranslations } from '@/hooks/useTranslations'
import { colors } from '@/themes/commerce/colors'
import { commerceTheme as theme } from '@/themes/commerce/theme'

import type { Theme } from '@mui/material'
import type { UnpackedSanityArticle } from '@/lib/queries/sanityQueryStrings'

const StyledChip = styled(Chip)(() => ({
  color: colors.background.component.default,
  backgroundColor: colors.background.component.subtle,
  borderRadius: 0,
  fontSize: '1rem',
  fontWeight: 700,
}))

export const ArticleClient = ({ sanityArticles }: { sanityArticles: UnpackedSanityArticle[] }) => {
  const { siteContext } = useSiteContext()
  const { data: session } = useSession()
  const { language } = useTranslations()

  const article = getArticleFromSanityArticles(sanityArticles, siteContext, language, session)
  const backgroundImage = resolveSanityImage(article?.backgroundImage)

  if (!article?.content) {
    return null
  }

  return (
    <Box
      sx={(theme: Theme) =>
        backgroundImage
          ? {
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% auto',
              backgroundImage: getResponsiveBackgroundImage(backgroundImage, theme),
            }
          : {}
      }
    >
      <Container
        maxWidth={backgroundImage ? 'lg' : 'xl'}
        sx={{
          backgroundColor: theme.palette.background.light,
          mt: backgroundImage ? 8.5 : 0,
          pb: 3,
        }}
      >
        <Box sx={{ py: 2 }}>
          <BreadcrumbsCustom title={article?.title} />
        </Box>
        {article?.tag && (
          <Grid item xs={12} sm={12} md={8} lg={8} sx={{ backgroundColor: theme.palette.background.light }}>
            <StyledChip label={article?.tag} />
          </Grid>
        )}

        {article.content &&
          Object.keys(article.content).map(
            (_key, index) =>
              article.content?.[index] != null && (
                <ContentSanityComponent sanityComponent={article.content[index]} key={index} index={index} />
              )
          )}
      </Container>
    </Box>
  )
}
